import { COUNT_GROUPS, groupTotal } from '@/shared/lib/statusCounts';
import { PROJECT_STATUS_TONES, type StatusTone } from '@/shared/constants/statuses';
import type { StatusCounts } from '@/shared/types/domain';

/**
 * A aritmética dos gráficos do painel, separada do desenho.
 *
 * Está aqui, e não dentro dos componentes, porque é a parte que erra em
 * silêncio: uma barra desenhada com a porcentagem trocada continua parecendo um
 * gráfico. O componente é conferido olhando; isto é conferido por teste.
 */

/**
 * Altura mínima de uma coluna que NÃO é zero, em porcentagem.
 *
 * Com um máximo de 12, uma coluna de 1 daria 8% — três pixels numa caixa de 40,
 * indistinguível de vazio. O piso existe para que "teve um projeto" nunca se
 * pareça com "não teve nenhum".
 *
 * ⚠️ **O piso não se aplica ao zero, e essa é a regra inteira.** Zero devolve
 * zero e a coluna não é desenhada. Um piso universal faria todo mês vazio exibir
 * um toco de barra — o gráfico afirmaria atividade onde não houve, que é
 * exatamente o tipo de mentira que este painel existe para não contar.
 */
const MIN_VISIBLE_PERCENT = 8;

/**
 * Converte um valor em altura percentual contra o máximo da série.
 *
 * `max <= 0` devolve 0 em vez de dividir por zero: série inteiramente vazia é
 * um gráfico sem barra nenhuma, não um `NaN` atravessando o `style`.
 */
export function toPercent(value: number, max: number): number {
  if (value <= 0 || max <= 0) return 0;
  return Math.max(MIN_VISIBLE_PERCENT, (value / max) * 100);
}

export interface ToneSegment {
  key: string;
  label: string;
  tone: StatusTone;
  value: number;
}

/**
 * Os pedaços coloridos de uma barra, na ordem de `COUNT_GROUPS` e **sem os
 * zeros**.
 *
 * A ordem é a mesma que o `StatusTiles` e o `StatusPills` já usam — rascunho,
 * aguardando, ajustes, aprovados, concluídos —, e ela é do vocabulário do
 * sistema, não de cada tela. Segmento zerado sai fora pelo mesmo motivo que o
 * tile zerado sai: ele ocuparia lugar na legenda dizendo que não há nada a
 * dizer.
 *
 * O tom vem do PRIMEIRO status do grupo. Hoje todo grupo tem um só; se algum
 * vier a ter dois, eles necessariamente compartilham o tom — é o que os torna
 * um grupo.
 */
export function toneSegments(counts: StatusCounts): ToneSegment[] {
  return COUNT_GROUPS.map((group) => ({
    key: group.key,
    label: group.label,
    tone: PROJECT_STATUS_TONES[group.statuses[0]],
    value: groupTotal(counts, group.statuses),
  })).filter((segment) => segment.value > 0);
}

/**
 * A fatia de cada segmento DENTRO da barra, em porcentagem — serve à largura na
 * barra horizontal e à altura na coluna empilhada.
 *
 * **Sem piso, ao contrário de `toPercent`, e a assimetria é o ponto.** O piso
 * de lá existe para a barra inteira não sumir contra o máximo da série; aqui os
 * segmentos dividem um espaço já dimensionado, e dar mínimo a um deles roubaria
 * proporcional dos vizinhos — a barra deixaria de somar 100% e a altura da
 * coluna pararia de significar volume. Foi exatamente o defeito da primeira
 * versão do `ColumnChart`: com o piso por segmento, três faixas de 1 ficavam
 * mais altas que uma faixa de 3.
 *
 * Segmento pequeno demais para enxergar continua legível pelo número, que está
 * sempre ao lado — na legenda da barra, no topo da coluna.
 */
export function segmentShares(segments: readonly ToneSegment[]): number[] {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);
  if (total <= 0) return segments.map(() => 0);
  return segments.map((segment) => (segment.value / total) * 100);
}

/**
 * A mediana de uma amostra. Devolve `null` para amostra vazia — e é `null`, não
 * zero, porque "nenhuma decisão no período" e "decidido no mesmo dia" são
 * respostas diferentes, e um zero as confundiria na tela.
 */
export function median(values: readonly number[]): number | null {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);

  // O `?? null` não é cerimônia com o `noUncheckedIndexedAccess`: ele É a
  // guarda da amostra vazia. Com zero elementos, `sorted[0]` é `undefined`, e
  // é daí que sai o `null` que a tela distingue de "decidido no mesmo dia".
  const upper = sorted[middle] ?? null;
  if (upper === null || sorted.length % 2 !== 0) return upper;

  const lower = sorted[middle - 1] ?? upper;
  return (lower + upper) / 2;
}
